import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { CookiesProvider } from "react-cookie";
import { PersistGate } from "redux-persist/integration/react";

import "./assets/stylesheets/index.scss";
import "./utils/sentry";
import "./utils/i18n";
import { setupStore } from "./store";
import App from "./App";
import setupOnlineStateRelay from "./utils/swRelay";
import setupOfflineAnalytics from "./utils/swAnalytics";
import { isOfflineEnabled } from "./utils/features";

const { store, persistor } = setupStore();

if (process.env.NODE_ENV === "development" && isOfflineEnabled()) {
  import("./utils/devOfflineWarning");
}

if (!isOfflineEnabled() && "serviceWorker" in navigator) {
  // Clean up any previously installed SW and its caches when the feature is disabled
  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => registrations.forEach((r) => r.unregister()));
  caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)));
} else if (isOfflineEnabled() && "serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/service-worker.js")
    .catch((err) => console.error("Service worker registration failed:", err));

  setupOnlineStateRelay(navigator.serviceWorker);
  setupOfflineAnalytics(navigator.serviceWorker);
}

const supportsContainerQueries = "container" in document.documentElement.style;
if (!supportsContainerQueries) {
  import("container-query-polyfill");
}

window.addEventListener("unhandledrejection", function (event) {
  console.error("Unhandled promise rejection:", event.reason);
});

const root = (AppContent, siteOidc) => {
  const div = document.getElementById("root");
  const root = createRoot(div);

  root.render(
    <CookiesProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Suspense fallback={<></>}>
            <App ContentComponent={AppContent} siteOidc={siteOidc} />
          </Suspense>
        </PersistGate>
      </Provider>
    </CookiesProvider>,
  );
};

export default root;
